import { Button } from '@orbind/ui'
import { useState, type CSSProperties, type ReactNode } from 'react'
import './form-screen.css'

/**
 * Padrao de tela de edicao do Orbind (referencia visual: Conta Azul, escolha
 * do usuario em 2026-08-15):
 *
 *  - cabecalho fino: "Titulo — subtitulo" + botao de fechar (X);
 *  - secoes empilhadas (FormSection), cada uma recolhivel pelo chevron;
 *  - campos distribuidos em colunas na mesma linha (FormRow), rotulo em cima;
 *  - barra fixa no rodape (padrao 2026-08-30, referencia dada pelo usuario):
 *    a acao DESTRUTIVA ("Excluir ...") fica sozinha a ESQUERDA, longe do resto;
 *    estado de alteracoes, Cancelar e Salvar ficam juntos a DIREITA. Separar a
 *    exclusao das acoes do dia a dia evita o clique errado. Enter salva.
 *
 * Toda tela de cadastro/edicao nova ou migrada usa ESTES componentes — nada
 * de modal e nada de layout proprio por tela.
 */

export function FormScreen(props: {
  title: string
  /** Aparece esmaecido depois do titulo: "Editar quadro — Sustentação". */
  subtitle?: string
  /** Fechar/Cancelar. Sem ele a tela nao tem X nem botao Cancelar. */
  onClose?: () => void
  /** Ha alteracao pendente? Controla o indicador e o texto do rodape. */
  dirty?: boolean
  /** Texto do indicador quando NAO ha alteracao (padrao "Tudo salvo"). */
  dirtyHint?: string
  saveLabel?: string
  saveDisabled?: boolean
  saving?: boolean
  onSubmit: () => void
  /** Acoes extras no rodape, entre o indicador e o Salvar (ex.: Descartar). */
  footerExtra?: ReactNode
  /**
   * Exclusao do registro. Presente, vira o botao de PERIGO sozinho a esquerda
   * do rodape ("Excluir tipo de item"). Ausente, a tela nao oferece exclusao
   * por aqui — e o caso das telas de criacao.
   */
  onDelete?: () => void
  /** Rotulo do botao de exclusao (padrao "Excluir"). */
  deleteLabel?: string
  /** Desabilita a exclusao (ex.: registro do sistema ou em uso). */
  deleteDisabled?: boolean
  /** Motivo de a exclusao estar bloqueada — vira o title do botao. */
  deleteHint?: string
  /** Acoes NA LINHA DO TITULO, alinhadas a direita antes do X. */
  headerActions?: ReactNode
  /** Esconde a barra do rodape (quando as acoes moram no cabecalho). */
  hideFooter?: boolean
  children: ReactNode
}) {
  return (
    <div className="fscr">
      <header className="fscr__head">
        <h1 className="fscr__title">
          {props.title}
          {props.subtitle ? <span className="fscr__subtitle"> — {props.subtitle}</span> : null}
        </h1>
        {props.headerActions ? <span className="fscr__head-actions">{props.headerActions}</span> : null}
        {props.onClose ? (
          <button type="button" className="fscr__close" aria-label="Fechar" title="Fechar" onClick={props.onClose}>
            <i className="fas fa-times" aria-hidden />
          </button>
        ) : null}
      </header>

      <form
        className="fscr__form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          props.onSubmit()
        }}
      >
        <div className="fscr__body">{props.children}</div>

        {props.hideFooter ? null : (
        <footer className="fscr__bar">
          {/* PERIGO sozinho a esquerda: fica longe de Cancelar/Salvar */}
          <span>
            {props.onDelete ? (
              <button
                type="button"
                className="fscr__delete"
                disabled={props.deleteDisabled}
                title={props.deleteHint}
                onClick={props.onDelete}
              >
                <i className="fas fa-trash-alt" aria-hidden />
                {props.deleteLabel ?? 'Excluir'}
              </button>
            ) : null}
          </span>
          <span className="fscr__bar-right">
            <span className={`fscr__dirty${props.dirty ? ' fscr__dirty--on' : ''}`} role="status">
              <span className="fscr__dirty-dot" aria-hidden />
              {props.dirty ? 'Alterações não salvas' : (props.dirtyHint ?? 'Tudo salvo')}
            </span>
            {props.footerExtra}
            {props.onClose ? (
              <Button variant="secondary" onClick={props.onClose}>
                Cancelar
              </Button>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              icon="fas fa-check"
              loading={props.saving}
              disabled={props.saveDisabled}
              title={!props.dirty ? 'Nada para salvar — altere algum campo primeiro' : undefined}
            >
              {props.saveLabel ?? 'Salvar'}
            </Button>
          </span>
        </footer>
        )}
      </form>
    </div>
  )
}

/** Secao do formulario: card com titulo e chevron de recolher, como a referencia. */
export function FormSection(props: {
  title: ReactNode
  /** Acoes ao lado do chevron (ex.: botao "Recolher" de subareas). */
  right?: ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  /** `danger` pinta o titulo/borda para zonas de perigo. */
  tone?: 'default' | 'danger'
  children: ReactNode
}) {
  const [open, setOpen] = useState(props.defaultOpen ?? true)
  const collapsible = props.collapsible ?? true
  // sem titulo, sem acoes e sem chevron nao ha o que mostrar no cabecalho
  const mostrarCabecalho = Boolean(props.title) || Boolean(props.right) || collapsible
  return (
    <section className={`fscr__section${props.tone === 'danger' ? ' fscr__section--danger' : ''}`}>
      {mostrarCabecalho ? (
      <div className="fscr__section-head">
        <h2 className="fscr__section-title">{props.title}</h2>
        <span className="fscr__section-right">
          {props.right}
          {collapsible ? (
            <button
              type="button"
              className="fscr__chev"
              aria-expanded={open}
              aria-label={open ? 'Recolher seção' : 'Expandir seção'}
              onClick={() => setOpen((o) => !o)}
            >
              <i className={open ? 'fas fa-chevron-up' : 'fas fa-chevron-down'} aria-hidden />
            </button>
          ) : null}
        </span>
      </div>
      ) : null}
      {open ? props.children : null}
    </section>
  )
}

/**
 * Linha de campos: colunas na MESMA linha (rotulo em cima, via FieldBlock).
 * `cols` define as larguras ("2fr 1fr 1fr"); sem ele, auto-fit de 220px.
 */
export function FormRow(props: { cols?: string; children: ReactNode }) {
  const style: CSSProperties | undefined = props.cols ? { gridTemplateColumns: props.cols } : undefined
  return (
    <div className="fscr__row" style={style}>
      {props.children}
    </div>
  )
}
